import { useSuspenseQuery } from "@apollo/client/react";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { DreamSearchProgramItems } from "@portal/app/health/_components/components/DreamSearchResult/DreamSearchProgramItems";
import { getUrls } from "@portal/app/_common/lib/urls";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { SponsoredResultsDisclaimer } from "@portal/app/health/_components/components/DreamSearchResult/SponsoredResultsDisclaimer";
import { HomePageProps } from "@portal/app/page";
import { redirect } from "next/navigation";
import { isDegradableGeocodeError } from "@portal/app/_common/lib/clientErrorUtils";
import { FALLBACK_ZIP } from "@portal/app/_common/lib/requestUtils";

import { useEffect } from "react";
import { gql } from "@apollo/client/core";
import { DreamSearchForHomePageDocument } from "./generated";

void gql`
  query dreamSearchForHomePage(
    $dreamSearchFilters: DreamSearchFilters!
    $geo: DreamSearchGeo!
    $first: Int!
    $after: Int
    $complianceExclusionTags: [String!]!
  ) {
    dreamSearchItemsConnection(
      filter: $dreamSearchFilters
      geo: $geo
      first: $first
      after: $after
      complianceExclusionTags: $complianceExclusionTags
      includeFilterSettings: false
      includeOtherRecommendedItems: false
    ) {
      nodes {
        ...DreamSearchItemFragment
      }
      city
      state
      zip
    }
  }
`;

export const HomeDreamSearchSection = ({
  variables,
  industry,
  setZip,
  shouldPromptForLocation = false,
}: Pick<HomePageProps, "variables" | "industry"> & {
  setZip: (zip: string) => void;
  shouldPromptForLocation?: boolean;
}) => {
  const urls = getUrls();
  const analytics = useMixpanel();
  const { data, error } = useSuspenseQuery(DreamSearchForHomePageDocument, {
    variables,
    errorPolicy: "all",
  });

  // Redirect on invalid address (e.g., non-US visitor, unrecognized ZIP) or
  // a geocode outage. Uses FALLBACK_ZIP to prevent infinite redirect loops.
  if (isDegradableGeocodeError(error)) {
    redirect(
      urls.DreamSearch({ industry, zip: FALLBACK_ZIP, shouldPromptForLocation: true })
    );
  }

  useEffect(() => {
    if (data?.dreamSearchItemsConnection?.zip) {
      setZip(data.dreamSearchItemsConnection.zip);
    }
  }, [data?.dreamSearchItemsConnection?.zip, setZip]);

  const { city, state, zip } = data?.dreamSearchItemsConnection ?? {};
  const cityAndStateText = city && state ? `${city}, ${state} ` : "";
  const showingClassesFromText = zip ? `${cityAndStateText}${zip}` : "";
  return (
    <Section>
      <DreamSearchPreviewCards>
        <DreamSearchProgramItems
          items={data?.dreamSearchItemsConnection?.nodes ?? []}
          eventName="Homepage - Clicked Dreamsearch Card"
        />
      </DreamSearchPreviewCards>
      <SponsoredResultsDisclaimerContainer>
        <SponsoredResultsDisclaimer />
      </SponsoredResultsDisclaimerContainer>
      {showingClassesFromText && (
        <ShowingClassesFromText variant="disclaimer">
          Showing classes from {showingClassesFromText}
        </ShowingClassesFromText>
      )}
      <ExploreMoreButton
        variant="contained"
        href={urls.DreamSearch({
          industry,
          zip,
          shouldPromptForLocation,
        })}
        onClick={() =>
          analytics.track("Homepage - Navigate to DreamSearch", {
            industry,
            zip,
          })
        }
      >
        Explore more classes
      </ExploreMoreButton>
    </Section>
  );
};

const Section = styled("section")`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
export const DreamSearchPreviewCards = styled("div")`
  margin: 2.5rem 0 2rem;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
  padding: 0 1rem;

  ${({ theme }) => theme.breakpoints.down("md")} {
    margin-top: 2rem;
    grid-template-columns: 1fr 1fr;
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    margin-top: 2rem;
    grid-template-columns: 1fr;
  }
`;
const ShowingClassesFromText = styled(Typography)`
  margin-bottom: 0.4rem;
`;

const ExploreMoreButton = styled(Button)`
  font-weight: bold;
  width: 20rem;
  background: #04299e;
  color: white;

  &:hover {
    background: #031f78;
  }
`;

const SponsoredResultsDisclaimerContainer = styled("div")`
  margin-bottom: 1rem;
`;
